import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff, CheckCheck, Check, Ticket, RefreshCw, PlusCircle, MessageSquare, User } from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import { formatRelativeTime } from '../../../utils';
import type { AppNotification } from '../../../shared/types';
import styles from './NotificationDropdown.module.scss';

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    isLoading,
    unreadOnlyFilter,
    setUnreadOnlyFilter,
    markAsRead,
    markAllAsRead,
    isMarkingAllRead,
  } = useNotifications();

  if (!isOpen) return null;

  const handleNotificationClick = (notification: AppNotification) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    if (notification.ticketId) {
      const typeStr = (notification.type || '').toUpperCase();
      const titleStr = (notification.title || '').toLowerCase();
      const msgStr = (notification.message || '').toLowerCase();
      const isReply =
        typeStr.includes('REPLY') ||
        typeStr.includes('MESSAGE') ||
        titleStr.includes('reply') ||
        titleStr.includes('message') ||
        msgStr.includes('reply') ||
        msgStr.includes('message');

      navigate(`/support-tickets/${notification.ticketId}`, {
        state: { activeTab: isReply ? 'conversation' : 'overview' },
      });
      onClose();
    }
  };

  const handleMarkSingleRead = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    markAsRead(id);
  };

  const handleMarkAllRead = () => {
    markAllAsRead();
  };

  const renderIcon = (type: string) => {
    switch (type) {
      case 'TICKET_CREATED':
      case 'TICKET_NEW':
        return (
          <div className={`${styles.iconWrapper} ${styles.iconWrapperCreated}`}>
            <PlusCircle size={18} />
          </div>
        );
      case 'TICKET_ASSIGNED':
        return (
          <div className={`${styles.iconWrapper} ${styles.iconWrapperAssigned}`}>
            <Ticket size={18} />
          </div>
        );
      case 'TICKET_STATUS_CHANGED':
        return (
          <div className={`${styles.iconWrapper} ${styles.iconWrapperStatus}`}>
            <RefreshCw size={18} />
          </div>
        );
      case 'TICKET_REPLY':
      case 'TICKET_MESSAGE':
        return (
          <div className={`${styles.iconWrapper} ${styles.iconWrapperReply}`}>
            <MessageSquare size={18} />
          </div>
        );
      default:
        return (
          <div className={`${styles.iconWrapper} ${styles.iconWrapperDefault}`}>
            <Bell size={18} />
          </div>
        );
    }
  };

  return (
    <div className={styles.dropdown} role="dialog" aria-label="Notifications Dropdown">
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTitleGroup}>
          <h3 className={styles.title}>Notifications</h3>
          {unreadCount > 0 && <span className={styles.unreadCountBadge}>{unreadCount} unread</span>}
        </div>
        {unreadCount > 0 && (
          <button
            type="button"
            className={styles.markAllBtn}
            onClick={handleMarkAllRead}
            disabled={isMarkingAllRead}
            aria-label="Mark all as read"
          >
            <CheckCheck size={14} />
            <span>Mark all read</span>
          </button>
        )}
      </div>

      {/* Filter Tabs */}
      <div className={styles.filterTabs}>
        <button
          type="button"
          className={`${styles.tabBtn} ${!unreadOnlyFilter ? styles.tabBtnActive : ''}`}
          onClick={() => setUnreadOnlyFilter(false)}
        >
          All
        </button>
        <button
          type="button"
          className={`${styles.tabBtn} ${unreadOnlyFilter ? styles.tabBtnActive : ''}`}
          onClick={() => setUnreadOnlyFilter(true)}
        >
          Unread {unreadCount > 0 ? `(${unreadCount})` : ''}
        </button>
      </div>

      {/* Content List */}
      <div className={styles.listContent}>
        {isLoading ? (
          <div className={styles.loadingState}>
            <span>Loading notifications...</span>
          </div>
        ) : notifications.length === 0 ? (
          <div className={styles.emptyState}>
            <BellOff size={32} />
            <p>No notifications</p>
            <span>{unreadOnlyFilter ? 'You have no unread notifications' : "You're all caught up!"}</span>
          </div>
        ) : (
          notifications.map((item: AppNotification) => {
            const cleanMessage = (item.message || '').replace(/\s+/g, ' ').trim();
            return (
              <div
                key={item.id}
                className={`${styles.notificationItem} ${!item.isRead ? styles.notificationItemUnread : ''} ${
                  item.ticketId ? styles.notificationItemClickable : ''
                }`}
                onClick={() => handleNotificationClick(item)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleNotificationClick(item);
                  }
                }}
              >
                {renderIcon(item.type)}
                <div className={styles.itemMain}>
                  <div className={styles.itemHeader}>
                    <span className={styles.itemTitle}>{item.title}</span>
                    {!item.isRead && <span className={styles.unreadDot} title="Unread" />}
                  </div>
                  <div className={styles.itemMessage}>{cleanMessage}</div>
                  <div className={styles.itemFooter}>
                    <div className={styles.itemMeta}>
                      <span className={styles.itemTime}>{formatRelativeTime(item.createdAt)}</span>
                      {item.userName && (
                        <>
                          <span className={styles.metaDot}>•</span>
                          <span className={styles.itemUser}>
                            <User size={10} className={styles.userIcon} />
                            <span>{item.userName}</span>
                          </span>
                        </>
                      )}
                    </div>
                    {!item.isRead && (
                      <button
                        type="button"
                        className={styles.markReadBtn}
                        onClick={(e) => handleMarkSingleRead(e, item.id)}
                        title="Mark as read"
                        aria-label="Mark notification as read"
                      >
                        <Check size={12} />
                        <span>Mark read</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default NotificationDropdown;
